<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { RouterLink } from 'vue-router';
import api from '@/services/api';
import NewsCard from '@/components/NewsCard.vue';
import type { BlogPost, PaginatedResponse } from '@shared/types';

const latestNews = ref<BlogPost[]>([]);
const isLoading = ref(true);

const productCategories = [
  {
    id: 'filterzellen',
    name: 'Filterzellen',
    description: 'Hohe Staubspeicherkapazität und lange Standzeiten für verschiedenste Anwendungsbereiche.',
    image: '/images/products/filterzellen-01.jpg'
  },
  {
    id: 'panelfilter',
    name: 'Panelfilter',
    description: 'Robust, effizient und vielseitig einsetzbar in Lüftungs- und Klimaanlagen.',
    image: '/images/products/panelfilter-01.jpg'
  },
  {
    id: 'rollenware',
    name: 'Rollenware',
    description: 'Filtermedien als Rollenware für individuelle Zuschnitte und Sonderanfertigungen.',
    image: '/images/products/filterzellen-04.jpg'
  },
  {
    id: 'taschenfilter',
    name: 'Taschenfilter',
    description: 'Maximale Filterleistung bei minimalem Druckverlust für Feinfiltration.',
    image: '/images/products/taschenfilter-01.jpg'
  }
];

const capabilities = [
  { label: 'Made in Germany', detail: 'Höchste Qualitätsstandards aus deutscher Fertigung' },
  { label: 'Innovativ', detail: 'Modernste Filtermedien-Technologien' },
  { label: 'Partnerschaftlich', detail: 'Persönliche Beratung und individuelle Lösungen' },
  { label: 'Zuverlässig', detail: 'Schnelle Lieferung und konstante Verfügbarkeit' }
];

onMounted(async () => {
  try {
    const response = await api.get<{ success: boolean; data: PaginatedResponse<BlogPost> }>('/api/blog?limit=3');
    if (response.data.success && response.data.data) {
      latestNews.value = response.data.data.items;
    }
  } catch (error) {
    console.error('Error fetching news:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="home-page">
    <!-- Hero -->
    <section class="hero">
      <div class="hero-inner container">
        <div class="hero-content animate-fade-in-up">
          <span class="overline">M&R Filtermedien</span>
          <h1 class="hero-title">
            Qualität in Filtration
          </h1>
          <p class="hero-description">
            Ihr zuverlässiger Partner für hochwertige Filtermedien.
            Innovation, Qualität und Service seit vielen Jahren.
          </p>
          <div class="hero-actions">
            <RouterLink to="/produkte" class="btn btn-primary btn-lg">
              Produkte entdecken
            </RouterLink>
            <RouterLink to="/kontakt" class="btn btn-secondary btn-lg">
              Kontakt aufnehmen
            </RouterLink>
          </div>
        </div>
        <div class="hero-visual animate-fade-in delay-2">
          <img src="/mr-logo.svg" alt="M&R Filtermedien Logo" class="hero-logo" width="287" height="287" />
        </div>
      </div>
    </section>

    <!-- Capabilities — horizontal strip, not card grid -->
    <section class="capabilities">
      <div class="container">
        <div class="capabilities-row">
          <div
            v-for="(cap, i) in capabilities"
            :key="cap.label"
            class="capability-item animate-fade-in-up"
            :class="`delay-${i + 1}`"
          >
            <span class="capability-label">{{ cap.label }}</span>
            <span class="capability-detail">{{ cap.detail }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Products — asymmetric featured layout -->
    <section class="products section-lg">
      <div class="container">
        <div class="products-header">
          <span class="overline">Sortiment</span>
          <h2>Unsere Produkte</h2>
        </div>

        <div class="products-grid">
          <RouterLink
            v-for="(category, index) in productCategories"
            :key="category.id"
            :to="`/produkte#${category.id}`"
            class="product-item"
            :class="{ 'product-item--featured': index === 0 }"
          >
            <div class="product-item-visual">
              <img :src="category.image" :alt="category.name" class="product-image" loading="lazy">
            </div>
            <div class="product-item-info">
              <h3>{{ category.name }}</h3>
              <p>{{ category.description }}</p>
              <span class="product-item-link">
                Mehr erfahren
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </span>
            </div>
          </RouterLink>
        </div>

        <div class="products-footer">
          <RouterLink to="/produkte" class="btn btn-secondary">
            Alle Produkte ansehen
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- About Teaser — 70/30 split -->
    <section class="about-teaser">
      <div class="container">
        <div class="about-teaser-layout">
          <div class="about-teaser-content">
            <span class="overline">Über uns</span>
            <h2>Kompetenz in Filtermedien</h2>
            <p class="about-teaser-text">
              Seit vielen Jahren sind wir Ihr kompetenter Ansprechpartner für
              hochwertige Filtermedien. Mit unserem Fachwissen und unserer
              Erfahrung unterstützen wir Sie bei der Auswahl der optimalen
              Filterlösung für Ihre Anwendung.
            </p>
            <p class="about-teaser-text">
              Unsere Produkte werden nach höchsten Qualitätsstandards gefertigt
              und erfüllen alle relevanten Normen und Vorschriften.
            </p>
            <RouterLink to="/unternehmen" class="btn btn-ghost">
              Mehr über uns erfahren →
            </RouterLink>
          </div>
          <div class="about-teaser-visual">
            <div class="placeholder-visual placeholder-visual--md">
              <span>M&R</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- News -->
    <section class="news section bg-secondary">
      <div class="container">
        <div class="news-header">
          <div>
            <span class="overline">Aktuelles</span>
            <h2>Neuigkeiten</h2>
          </div>
          <RouterLink to="/aktuelles" class="btn btn-secondary hide-mobile">
            Alle Neuigkeiten
          </RouterLink>
        </div>

        <div v-if="isLoading" class="news-loading" role="status">
          <p>Lade Neuigkeiten...</p>
        </div>

        <div v-else-if="latestNews.length > 0" class="news-grid">
          <NewsCard
            v-for="post in latestNews"
            :key="post.id"
            :post="post"
          />
        </div>

        <div v-else class="news-empty">
          <p>Derzeit keine Neuigkeiten verfügbar.</p>
        </div>

        <div class="show-mobile-only mt-8">
          <RouterLink to="/aktuelles" class="btn btn-secondary news-mobile-btn">
            Alle Neuigkeiten
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-layout">
          <div>
            <h2>Haben Sie Fragen?</h2>
            <p>
              Wir beraten Sie gerne zu unseren Produkten und finden die
              optimale Lösung für Ihre Anforderungen.
            </p>
          </div>
          <RouterLink to="/kontakt" class="btn btn-primary btn-lg">
            Jetzt Kontakt aufnehmen
          </RouterLink>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* ============================================
   Hero — commanding, asymmetric
   ============================================ */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding-top: var(--nav-height);
  background: var(--bg-primary);
  overflow: hidden;
}

.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 60% at 70% 40%, rgba(70, 157, 213, 0.06) 0%, transparent 70%),
    radial-gradient(ellipse 50% 80% at 20% 80%, rgba(70, 157, 213, 0.04) 0%, transparent 60%);
  pointer-events: none;
}

.hero-inner {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 0.8fr;
  gap: var(--spacing-16);
  align-items: center;
  padding-top: var(--spacing-16);
  padding-bottom: var(--spacing-16);
}

.hero-content {
  max-width: 640px;
}

.hero-content .overline {
  color: var(--color-primary);
  margin-bottom: var(--spacing-6);
  display: block;
}

.hero-title {
  font-size: clamp(3rem, 6vw, var(--font-size-6xl));
  color: var(--text-primary);
  margin-bottom: var(--spacing-6);
  line-height: 1.05;
  letter-spacing: var(--letter-spacing-tighter);
  font-weight: var(--font-weight-bold);
}

.hero-description {
  font-size: var(--font-size-lg);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-10);
  max-width: 480px;
  line-height: var(--line-height-relaxed);
}

.hero-actions {
  display: flex;
  gap: var(--spacing-4);
  flex-wrap: wrap;
}

.hero-visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-logo {
  width: 100%;
  max-width: 360px;
  height: auto;
}

/* ============================================
   Capabilities — horizontal strip
   ============================================ */
.capabilities {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
  padding: var(--spacing-12) 0;
}

.capabilities-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--spacing-8);
}

.capability-item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  padding-left: var(--spacing-5);
  border-left: 2px solid var(--color-primary);
}

.capability-label {
  font-family: var(--font-family-display);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  letter-spacing: var(--letter-spacing-tight);
}

.capability-detail {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  line-height: var(--line-height-normal);
}

/* ============================================
   Products — asymmetric grid
   ============================================ */
.products-header {
  margin-bottom: var(--spacing-12);
}

.products-header .overline {
  margin-bottom: var(--spacing-3);
  display: block;
}

.products-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  grid-template-rows: repeat(3, auto);
  gap: var(--spacing-4);
}

.product-item {
  display: flex;
  flex-direction: column;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: border-color var(--transition-normal);
}

.product-item:hover {
  border-color: var(--color-primary);
}

.product-item--featured {
  grid-row: span 3;
}

.product-item-visual {
  flex-shrink: 0;
  background: var(--bg-tertiary);
}

.product-item--featured .product-item-visual {
  height: 280px;
}

.product-item:not(.product-item--featured) .product-item-visual {
  height: 140px;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-item-info {
  padding: var(--spacing-6);
  flex: 1;
  display: flex;
  flex-direction: column;
}

.product-item-info h3 {
  font-size: var(--font-size-xl);
  margin-bottom: var(--spacing-2);
}

.product-item-info p {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-4);
  flex: 1;
}

.product-item-link {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  transition: gap var(--transition-fast);
}

.product-item:hover .product-item-link {
  gap: var(--spacing-3);
}

.products-footer {
  margin-top: var(--spacing-10);
}

/* ============================================
   About Teaser — 60/40 asymmetric
   ============================================ */
.about-teaser {
  padding: var(--spacing-32) 0;
  border-top: 1px solid var(--border-color);
}

.about-teaser-layout {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: var(--spacing-16);
  align-items: center;
}

.about-teaser-content .overline {
  margin-bottom: var(--spacing-3);
  display: block;
}

.about-teaser-content h2 {
  margin-bottom: var(--spacing-6);
}

.about-teaser-text {
  font-size: var(--font-size-lg);
  color: var(--text-secondary);
  line-height: var(--line-height-relaxed);
}

.about-teaser-visual {
  aspect-ratio: 4 / 3;
  border-radius: var(--radius-xl);
  overflow: hidden;
}

.about-teaser .btn-ghost {
  margin-top: var(--spacing-4);
}

/* ============================================
   News — header with inline action
   ============================================ */
.news-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: var(--spacing-10);
}

.news-header .overline {
  margin-bottom: var(--spacing-3);
  display: block;
}

.news-header h2 {
  margin: 0;
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-6);
}

.news-loading,
.news-empty {
  text-align: center;
  padding: var(--spacing-12);
  color: var(--text-secondary);
}

.news-mobile-btn {
  width: 100%;
  justify-content: center;
}

/* ============================================
   Responsive
   ============================================ */
@media (max-width: 1024px) {
  .hero-inner {
    grid-template-columns: 1fr;
  }

  .hero-visual {
    display: none;
  }

  .capabilities-row {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-6);
  }

  .products-grid {
    grid-template-columns: 1fr 1fr;
  }

  .product-item--featured {
    grid-row: span 1;
  }

  .product-item--featured .product-item-visual {
    height: 180px;
  }

  .news-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .about-teaser-layout {
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-10);
  }

  .cta-layout {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }

  .cta-content p {
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .hero {
    min-height: auto;
    padding-top: var(--nav-height);
    padding-bottom: 0;
  }

  .hero-inner {
    padding-top: var(--spacing-12);
    padding-bottom: var(--spacing-12);
  }

  .capabilities-row {
    grid-template-columns: 1fr;
    gap: var(--spacing-6);
  }

  .products-grid {
    grid-template-columns: 1fr;
  }

  .product-item--featured .product-item-visual,
  .product-item:not(.product-item--featured) .product-item-visual {
    height: 160px;
  }

  .news-grid {
    grid-template-columns: 1fr;
  }

  .about-teaser-layout {
    grid-template-columns: 1fr;
  }

  .about-teaser-visual {
    order: -1;
  }

  .about-teaser {
    padding: var(--spacing-16) 0;
  }

  .hero-actions {
    flex-direction: column;
  }
}
</style>
